'use client';
import { useState, useId, useRef } from 'react';
import NextImage from 'next/image';
import { motion, AnimatePresence, useInView } from 'motion/react';
import { cn } from 'lib/utils';
import { type ImageField, type LinkField } from 'lib/types';
import { trackEvent } from 'lib/analytics';
import {
  type BloomProps,
  Bloom,
  HeaderSection,
  type HeaderSectionProps,
  Icon,
  Link,
  RichText,
  Wrapper,
} from 'ui';

export type AccordionSplitProps = {
  className?: string;
  eyebrow?: string;
  title?: string;
  intro?: string;
  headerAlignment?: HeaderSectionProps['alignment'];
  items: {
    title: string;
    content: string;
    link?: LinkField;
  }[];
  image: ImageField;
  alignment?: 'left' | 'right';
  bloom?: BloomProps;
};

const AccordionSplit = ({
  className,
  alignment = 'left',
  eyebrow,
  title,
  intro,
  headerAlignment,
  items,
  image,
  bloom,
}: AccordionSplitProps) => {
  const id = useId();
  const [openItem, setOpenItem] = useState<number | null>(null);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-20%' });

  const toggleItem = (index: number) => {
    setOpenItem(openItem === index ? null : index);
  };

  return (
    <motion.section {...{ ref }}>
      <Wrapper
        variant="page-full"
        className={cn('relative flex flex-col gap-10 md:gap-12', className)}
      >
        <Bloom {...bloom} />

        <HeaderSection
          content={intro}
          alignment={headerAlignment}
          {...{ eyebrow, title }}
        />

        <div
          className={cn(
            'relative flex flex-col-reverse gap-10 md:gap-12 xl:gap-20 md:items-start',
            {
              'md:flex-row': alignment === 'left',
              'md:flex-row-reverse': alignment === 'right',
            },
            className
          )}
        >
          {/* Content */}
          <div
            className="md:grow md:shrink md:basis-1/2 flex flex-col gap-4 relative z-2"
            role="region"
            aria-label="Accordion content"
          >
            {items.slice(0, 6).map((item, index) => {
              const isOpen = openItem === index;
              const contentId = `${id}-content-${index}`;
              const headerId = `${id}-header-${index}`;

              return (
                <motion.div
                  key={index}
                  className="bg-graphite-700 p-2 rounded-lg"
                  role="group"
                  initial={{ opacity: 0, x: alignment === 'left' ? -40 : 40 }}
                  animate={
                    isInView
                      ? { opacity: 1, x: 0 }
                      : { opacity: 0, x: alignment === 'left' ? -40 : 40 }
                  }
                  transition={{
                    duration: 0.6,
                    delay: 0.1 + index * 0.1,
                    ease: [0.25, 0.1, 0.25, 1],
                  }}
                >
                  <button
                    onClick={() => {
                      toggleItem(index);
                      if (item.title) {
                        trackEvent('accordion_header_clicked', {
                          cta: item.title,
                          location: 'accordion',
                          action: isOpen ? `close_item` : `open_item`,
                          selection: item.title,
                        });
                      }
                    }}
                    aria-expanded={isOpen}
                    aria-controls={contentId}
                    id={headerId}
                    type="button"
                    className="w-full p-2 md:p-4 flex items-center justify-between gap-4 md:gap-6 text-left group rounded-lg cursor-pointer"
                  >
                    <h3
                      className="text-p1 group-hover:text-athletic-200 transition-colors"
                      id={`${headerId}-title`}
                    >
                      {item.title}
                    </h3>
                    <Icon
                      name={isOpen ? 'minus' : 'plus'}
                      className="w-4 h-auto fill-clay-50 group-hover:fill-athletic-200 shrink-0"
                    />
                  </button>

                  <AnimatePresence>
                    {isOpen && (
                      <motion.div
                        id={contentId}
                        role="region"
                        aria-labelledby={headerId}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.3, ease: 'easeOut' }}
                        className="overflow-hidden"
                      >
                        <div className="max-md:pt-2 px-2 pb-2 md:px-4 md:pb-4 flex flex-col gap-6">
                          <RichText
                            content={item.content}
                            theme="neutral"
                            variant="basic"
                          />

                          {!!item.link?.href && (
                            <div>
                              <Link
                                href={item.link.href}
                                target={item.link.target}
                                variant="neutral"
                              >
                                <span>{item.link.text}</span>
                                <Icon
                                  name="carousel-arrow-right"
                                  className="inline-block align-middle ml-1.5 w-4 h-auto"
                                />
                              </Link>
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </div>

          {/* Image */}
          <div
            className="md:grow md:shrink md:basis-1/2 md:max-w-150 relative z-2"
            role="img"
            aria-label={image.alt || 'Accompanying illustration'}
          >
            <NextImage
              src={image.src}
              alt={image.alt}
              width={image.width}
              height={image.height}
              className="w-full h-auto object-contain rounded-lg"
              role={!image.alt ? 'presentation' : undefined}
            />
          </div>
        </div>
      </Wrapper>
    </motion.section>
  );
};
export default AccordionSplit;
